import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Info } from "@phosphor-icons/react";

/**
 * Read-only view of the plan × feature matrix. Editable per-tenant overrides
 * live on the workspace detail page (Features tab). This page is the "here's
 * what each plan includes" reference for the platform operator.
 */
const PLAN_COLS = ["free", "pro", "enterprise"];

export default function SuperAdminFeatures() {
    const [catalog, setCatalog] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        api.get("/features/catalog")
            .then((r) => setCatalog(r.data))
            .catch(() => toast.error("Failed to load feature catalog"))
            .finally(() => setLoading(false));
    }, []);

    if (loading) return <div className="max-w-6xl mx-auto p-6 text-sm text-[#A3A3A3]">Loading…</div>;
    if (!catalog) return null;

    // Group by category
    const grouped = catalog.features.reduce((acc, f) => {
        (acc[f.category] = acc[f.category] || []).push(f);
        return acc;
    }, {});

    return (
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-8" data-testid="superadmin-features-page">
            <p className="text-[10px] font-black uppercase tracking-[0.2em] text-splash mb-1">Plan capability matrix</p>
            <h1 className="font-heading text-3xl sm:text-4xl font-black tracking-tight mb-1">Features</h1>
            <p className="text-sm text-[#525252] mb-6">
                Baseline features per plan. Per-tenant overrides are set from each workspace&apos;s detail page.
            </p>

            <div className="bg-cobalt/5 border border-cobalt/20 p-3 text-xs text-[#525252] flex items-start gap-2 mb-4">
                <Info size={14} weight="bold" className="text-cobalt mt-0.5 flex-shrink-0" />
                <span>
                    This is the platform default. To grant a Pro feature to a specific free tenant (e.g. for a trial),
                    open the workspace and use the <b>Features</b> tab.
                </span>
            </div>

            {/* Mobile cards */}
            <div className="sm:hidden space-y-4">
                {Object.entries(grouped).map(([category, features]) => (
                    <div key={category}>
                        <p className="text-[10px] font-black uppercase tracking-wider text-cobalt mb-2 px-1">{category}</p>
                        <div className="bg-white border border-[#E5E5E5] divide-y divide-[#F5F5F5]">
                            {features.map((f) => (
                                <div key={f.key} data-testid={`features-card-${f.key}`} className="px-4 py-3">
                                    <p className="text-sm font-heading font-bold">{f.label}</p>
                                    <p className="text-[10px] font-mono text-[#A3A3A3] mb-2">{f.key}</p>
                                    <div className="grid grid-cols-3 gap-2 pt-1">
                                        {PLAN_COLS.map((p) => (
                                            <div key={p} data-testid={`features-cell-card-${f.key}-${p}`}
                                                 className="text-center border border-[#F5F5F5] py-1.5">
                                                <p className="text-[9px] font-black uppercase tracking-wider text-[#A3A3A3]">{p}</p>
                                                {catalog.plan_matrix[p][f.key] ? (
                                                    <span className="text-emerald-600 font-black">✓</span>
                                                ) : (
                                                    <span className="text-[#E5E5E5]">—</span>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                ))}
            </div>

            {/* Desktop table */}
            <div className="hidden sm:block bg-white border border-[#E5E5E5] overflow-x-auto">
                <table className="w-full text-sm min-w-[720px]" data-testid="features-matrix-table">
                    <thead className="bg-[#FAFAFA] border-b border-[#E5E5E5]">
                        <tr className="text-left">
                            <th className="px-4 py-3 text-[10px] font-black uppercase tracking-wider text-[#525252]">Feature</th>
                            {PLAN_COLS.map((p) => (
                                <th key={p} className="px-4 py-3 text-center text-[10px] font-black uppercase tracking-wider text-[#525252]">{p}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {Object.entries(grouped).map(([category, features]) => (
                            <React.Fragment key={category}>
                                <tr className="bg-[#FAFAFA]">
                                    <td colSpan="4" className="px-4 py-2 text-[10px] font-black uppercase tracking-wider text-cobalt">
                                        {category}
                                    </td>
                                </tr>
                                {features.map((f) => (
                                    <tr key={f.key} data-testid={`features-row-${f.key}`} className="border-b border-[#F5F5F5]">
                                        <td className="px-4 py-3">
                                            <p className="text-sm">{f.label}</p>
                                            <p className="text-[10px] font-mono text-[#A3A3A3]">{f.key}</p>
                                        </td>
                                        {PLAN_COLS.map((p) => (
                                            <td key={p} data-testid={`features-cell-${f.key}-${p}`} className="px-4 py-3 text-center">
                                                {catalog.plan_matrix[p][f.key] ? (
                                                    <span className="inline-block text-emerald-600 font-black">✓</span>
                                                ) : (
                                                    <span className="inline-block text-[#E5E5E5]">—</span>
                                                )}
                                            </td>
                                        ))}
                                    </tr>
                                ))}
                            </React.Fragment>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
